import { Box, Card, CardContent, CardHeader, Collapse, Grid, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { NotificationConfig } from './NotificationsSettings';
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslations } from 'hooks';
import { getTranslation } from 'common';

type NotificationSettingsProps = {
    notifications: NotificationConfig[],
    handleActiveToggle: (idmessage: number, channelName: string, active: boolean) => void,
    handleSectionToggle: (categoryPrefix: string, channelName: string, nextActive: boolean) => void,
    category: string,
    categoryKey: string,
    toggleCategory: (category: string) => void,
    expandedCategories: Record<string, boolean>,
    categoryIcon?: React.ReactNode,
    searchTerm?: string,
}

const CHANNELS = [
    { key: "Push", icon: <NotificationsIcon fontSize="small" /> },
    { key: "Email", icon: <MailOutlineIcon fontSize="small" /> },
];

const NOTIFICATION_TRANSLATION_KEYS: Record<string, string> = {
    // Activities
    'NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU':       'generic.notification.new_activity_for_you.caption',
    'NOTIFICATION.PROCESS_TOKENS_STATE_DOING':       'generic.notification.process_tokens_state_doing.caption',
    'NOTIFICATION.PROCESS_TOKENS_STATE_TODO':        'generic.notification.process_tokens_state_todo.caption',
    'NOTIFICATION.PROCESS_TOKENS_STATE_DONE':        'generic.notification.process_tokens_state_done.caption',
    'NOTIFICATION.PROCESS_TOKENS_STATE_TODO_AGAIN':  'generic.notification.process_tokens_state_todo_again.caption',
    'NOTIFICATION.PROCESS_TOKENS_UPDATED':           'generic.notification.process_tokens_updated.caption',
    'NOTIFICATION.PROCESSES_INSERTED':               'generic.notification.processes_inserted.caption',
    'NOTIFICATION.PROCESSES_UPDATED':                'generic.notification.processes_updated.caption',
    'NOTIFICATION.PROCESSES_ENDED':                  'generic.notification.processes_ended.caption',
    'NOTIFICATION.PROCESSES_DELETED':                'generic.notification.processes_deleted.caption',
    'NOTIFICATION.PROCESSES_TOKENS_INSERTED':        'generic.notification.processes_tokens_inserted.caption',
    'NOTIFICATION.PROCESSES_UPLOADS_INSERTED':       'generic.notification.processes_uploads_inserted.caption',
    'NOTIFICATION.PROCESSES_UPLOADS_UPDATED':        'generic.notification.processes_uploads_updated.caption',
    'NOTIFICATION.PROCESSES_UPLOADS_DELETED':        'generic.notification.processes_uploads_deleted.caption',
    'NOTIFICATION.COMMENTS_INSERTED':                'generic.notification.comments_inserted.caption',
    // Robots
    'NOTIFICATION.ROBOTS_INSERTED':                  'generic.notification.robots_inserted.caption',
    'NOTIFICATION.ROBOTS_DELETED':                   'generic.notification.robots_deleted.caption',
    'NOTIFICATION.ROBOTS_UPDATED':                   'generic.notification.robots_updated.caption',
    'NOTIFICATION.ROBOT_REMOVED_FROM_ORGANIZATION':  'generic.notification.robot_removed_from_organization.caption',
    // Process Models
    'NOTIFICATION.PROCESSES_MODEL_INSERTED':         'generic.notification.processes_model_inserted.caption',
    'NOTIFICATION.PROCESSES_MODEL_UPDATED':          'generic.notification.processes_model_updated.caption',
    'NOTIFICATION.PROCESSES_MODEL_PUBLISHED':        'generic.notification.processes_model_published.caption',
    'NOTIFICATION.PROCESSES_MODEL_DEPUBLISHED':      'generic.notification.processes_model_depublished.caption',
    'NOTIFICATION.PROCESSES_MODEL_DELETED':          'generic.notification.processes_model_deleted.caption',
    // SLA
    'NOTIFICATION.SLA_AT_RISK':                      'generic.notification.sla_at_risk.caption',
    'NOTIFICATION.SLA_BREACHED':                     'generic.notification.sla_breached.caption',
    'NOTIFICATION.SLA_NUDGE':                        'generic.notification.sla_nudge.caption',
    'NOTIFICATION.SLA_ESCALATION':                   'generic.notification.sla_escalation.caption',
    'NOTIFICATION.SLA_BREACH_AT_CLOSURE':            'generic.notification.sla_breach_at_closure.caption',
    'NOTIFICATION.SLA_DIGEST':                       'generic.notification.sla_digest.caption',
    // Organization
    'NOTIFICATION.EDIT.ORGANIZATION_UPDATED':                   'generic.notification.organizations_updated.caption',
    'NOTIFICATION.ORGANIZATION_USERS_UPDATED_SET_ADMIN_FALSE':  'generic.notification.organization_users_updated_set_admin_false.caption',
    'NOTIFICATION.ORGANIZATION_USERS_UPDATED_SET_ADMIN_TRUE':   'generic.notification.organization_users_updated_set_admin_true.caption',
    'NOTIFICATION.USER_ABANDONED_ORGANIZATION':                 'generic.notification.user_abandoned_organization.caption',
    'NOTIFICATION.USER_REMOVED_FROM_ORGANIZATION':              'generic.notification.user_removed_from_organization.caption',
    'NOTIFICATION.ORGANIZATION_USERS_UPDATED_SET_ACTIVE':       'generic.notification.organization_users_updated_set_active.caption',
    // Roles
    'NOTIFICATION.EDIT.ROLES_UPDATED':        'generic.notification.roles_updated.caption',
    'NOTIFICATION.EDIT.ROLES_DELETED':        'generic.notification.roles_deleted.caption',
    'NOTIFICATION.EDIT.ROLES_INSERTED':       'generic.notification.roles_inserted.caption',
    'NOTIFICATION.EDIT.USERS_ROLES_INSERTED': 'generic.notification.users_roles_inserted.caption',
    'NOTIFICATION.EDIT.USERS_ROLES_DELETED':  'generic.notification.users_roles_deleted.caption',
    // Users
    'NOTIFICATION.EDIT.USERS_GROUPS_INSERTED':         'generic.notification.users_groups_inserted.caption',
    'NOTIFICATION.EDIT.USERS_GROUPS_DELETED':          'generic.notification.users_groups_deleted.caption',
    'NOTIFICATION.EDIT.USERS_GROUPS_UPDATED':          'generic.notification.users_groups_updated.caption',
    'NOTIFICATION.USERS_GROUPS_UPDATED_SET_HOD_TRUE':  'generic.notification.users_groups_updated_set_hod_true.caption',
    'NOTIFICATION.USERS_GROUPS_UPDATED_SET_HOD_FALSE': 'generic.notification.users_groups_updated_set_hod_false.caption',
    // Groups
    'NOTIFICATION.EDIT.GROUPS_UPDATED':  'generic.notification.groups_updated.caption',
    'NOTIFICATION.EDIT.GROUPS_INSERTED': 'generic.notification.groups_inserted.caption',
    'NOTIFICATION.EDIT.GROUPS_DELETED':  'generic.notification.groups_deleted.caption',
};

const NotificationGroup: React.FC<NotificationSettingsProps> = ({
    handleActiveToggle, handleSectionToggle, notifications, category, toggleCategory,
    expandedCategories, categoryIcon, categoryKey, searchTerm = ""
}) => {
    const translations = useTranslations();
    const getLabel = (key: string) => getTranslation(translations, NOTIFICATION_TRANSLATION_KEYS[key] ?? key);

    const notificationsByMessage = useMemo(() => {
        const result: Record<string, Record<string, { id: number; idmessage: number; active: boolean }>> = {};
        notifications.forEach(notification => {
            if (!result[notification.iddescribeusersettings]) {
                result[notification.iddescribeusersettings] = {};
            }
            result[notification.iddescribeusersettings][notification.name] = {
                id: notification.id,
                idmessage: notification.idmessage,
                active: notification.active,
            };
        });
        return result;
    }, [notifications]);

    // Per-channel stats across ALL entries (for the select-all checkbox)
    const channelStats = useMemo(() => {
        const stats: Record<string, { allActive: boolean; someActive: boolean }> = {};
        CHANNELS.forEach(({ key: ch }) => {
            const values = Object.values(notificationsByMessage)
                .map(channels => channels[ch])
                .filter(Boolean);
            const activeCount = values.filter(v => v.active).length;
            stats[ch] = {
                allActive: values.length > 0 && activeCount === values.length,
                someActive: activeCount > 0,
            };
        });
        return stats;
    }, [notificationsByMessage]);

    const needle = searchTerm.trim().toLowerCase();
    const allEntries = Object.entries(notificationsByMessage);
    const entries = needle
        ? allEntries.filter(([key]) => getLabel(key).toLowerCase().includes(needle))
        : allEntries;

    if (needle && entries.length === 0) return null;

    const isExpanded = needle ? true : (expandedCategories[category] ?? true);

    return (
        <Grid item xs={12} key={category}>
            <Card
                sx={{
                    position: "relative",
                    border: "1px solid",
                    borderColor: (theme) =>
                        theme.palette.mode === "dark" ? theme.palette.grey[700] : "divider",
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => toggleCategory(category)}
                    data-testid={`expand-${categoryKey}`}
                    sx={{
                        position: "absolute",
                        right: 4,
                        top: 14,
                        zIndex: 1,
                        p: 0.5,
                    }}
                >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <CardHeader
                    avatar={categoryIcon}
                    title={
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ pr: 3 }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ cursor: "pointer" }}
                                onClick={() => toggleCategory(category)}
                            >
                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                    {category}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {needle
                                        ? `${entries.length} / ${allEntries.length}`
                                        : `(${entries.length})`}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                {CHANNELS.map(({ key, icon }) => {
                                    const { allActive, someActive } = channelStats[key] ?? {};
                                    const tooltipLabel = allActive ? `Deselect all ${key}` : `Select all ${key}`;
                                    return (
                                        <Tooltip key={key} title={tooltipLabel} placement="top">
                                            <Box
                                                width={72}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                gap={0.25}
                                            >
                                                <Box sx={{ color: "text.secondary", display: "inline-flex" }}>{icon}</Box>
                                                <Switch
                                                    size="small"
                                                    data-testid={`notif-section-${key}-${categoryKey}`}
                                                    checked={someActive ?? false}
                                                    onChange={() => handleSectionToggle(categoryKey, key, !allActive)}
                                                />
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    }
                    sx={{ pb: 0 }}
                />

                <Collapse in={isExpanded}>
                    <CardContent sx={{ pt: 1, pb: '12px !important' }}>
                        {/* Column headers */}
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            mb={0.5}
                            pr={0.5}
                            sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                        >
                            {CHANNELS.map(({ key, icon }) => (
                                <Box
                                    key={key}
                                    width={72}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    pb={0.25}
                                >
                                    <Box sx={{ color: "primary.main", display: "inline-flex" }}>{icon}</Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {key}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Notification rows */}
                        {entries.map(([messageKey, channels], idx) => (
                            <Box
                                key={messageKey}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                py={0.75}
                                px={0.5}
                                sx={{
                                    borderBottom: idx < entries.length - 1 ? "1px solid" : "none",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    transition: "background-color 0.15s",
                                    '&:hover': { backgroundColor: "action.hover" },
                                }}
                            >
                                <Typography variant="body2" sx={{ ml: 1, flex: 1, color: "text.primary" }}>
                                    {getLabel(messageKey)}
                                </Typography>
                                <Box display="flex" alignItems="center">
                                    {CHANNELS.map(({ key: channelName }) => {
                                        const ch = channels[channelName];
                                        return (
                                            <Box key={channelName} width={72} display="flex" justifyContent="center">
                                                {ch ? (
                                                    <Switch
                                                        size="small"
                                                        data-testid={`notif-${channelName}-${messageKey}`}
                                                        checked={ch.active}
                                                        onChange={() => handleActiveToggle(ch.idmessage, channelName, ch.active)}
                                                    />
                                                ) : (
                                                    <Box width={34} />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </CardContent>
                </Collapse>
            </Card>
        </Grid>
    );
};

export default NotificationGroup;
